import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Check, RefreshCw, Languages, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { VoiceInput } from "./VoiceInput";
import { useAiCtx } from "./aiContext";

export function FieldAI({ field, form, value, onInsert, label, ctx }) {
  const { t, lang } = useLang();
  const { can, isStaff } = useAuth();
  const c = useAiCtx(ctx);
  const [open, setOpen] = useState(false);
  const [ins, setIns] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  if (!isStaff || !can("ai_assistant")) return null;
  const go = async (mode, target = lang) => {
    setBusy(true);
    setLast([mode, target]);
    try {
      const { data } = await api.post("/ai/field", { form, field, mode, target, instruction: ins, value: mode === "write" ? value || "" : out || value || "", ...c });
      setOut(data.text);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => { setOpen(false); setOut(""); setIns(""); };
  return (
    <>
      <button type="button" className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#D9531E] hover:opacity-80 ml-2 align-middle" onClick={(e) => { e.preventDefault(); setOpen(true); }} data-testid={`field-ai-${form}-${field}`}><Sparkles className="w-3.5 h-3.5" /> IA</button>
      {open && <Dialog open onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="field-ai-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D9531E]" /> {label || field}</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <textarea className="inp !min-h-[72px] flex-1" value={ins} onChange={(e) => setIns(e.target.value)} placeholder={t("Explain it in your words (optional)…", "Explíquelo con sus palabras (opcional)…")} data-testid="field-ai-instruction" />
            <VoiceInput onText={(s) => setIns((x) => `${x} ${s}`.trim())} testid="field-ai-voice" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => go("write")} data-testid="field-ai-write">{t("Write", "Redactar")}</button>
            <button className="btn-outline btn-sm" disabled={busy || !(out || value)} onClick={() => go("improve")} data-testid="field-ai-improve">{t("Improve text", "Mejorar texto")}</button>
            <button className="btn-outline btn-sm" disabled={busy || !(out || value)} onClick={() => go("translate", "en")} data-testid="field-ai-to-en"><Languages className="w-4 h-4" /> EN</button>
            <button className="btn-outline btn-sm" disabled={busy || !(out || value)} onClick={() => go("translate", "es")} data-testid="field-ai-to-es"><Languages className="w-4 h-4" /> ES</button>
          </div>
          {busy && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {t("Writing…", "Redactando…")}</div>}
          {out && !busy && <>
            <textarea className="inp !min-h-[140px]" value={out} onChange={(e) => setOut(e.target.value)} data-testid="field-ai-result" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button className="btn-primary btn-sm" onClick={() => { onInsert(out); close(); }} data-testid="field-ai-insert"><Check className="w-4 h-4" /> {t("Insert", "Insertar")}</button>
              <button className="btn-outline btn-sm" onClick={() => { navigator.clipboard?.writeText(out); toast.success(t("Copied", "Copiado")); }} data-testid="field-ai-copy"><Copy className="w-4 h-4" /> {t("Copy", "Copiar")}</button>
              <button className="btn-outline btn-sm" onClick={() => last && go(...last)} data-testid="field-ai-regenerate"><RefreshCw className="w-4 h-4" /> {t("Regenerate", "Regenerar")}</button>
              <button className="btn-ghost btn-sm" onClick={close} data-testid="field-ai-cancel">{t("Cancel", "Cancelar")}</button>
            </div>
          </>}
        </DialogContent>
      </Dialog>}
    </>
  );
}
